import { Tab } from "@headlessui/react";
import clsx from "clsx";
import {
    Car,
    CheckCircle,
    IdentificationCard,
    Scroll,
    Truck,
} from "phosphor-react";
import {
    DashboardCategories,
    type DashboardCategoriesType,
    type DashboardContentSections,
    type GreyFleetAlertTitles,
    type DashboardAlertsData,
} from "../../models/api/admin/dashboard";
import DashboardAlertsTabContents from "./DashboardAlertsTabContents";
import { useIsExtraLargeScreen } from "../../hooks/screenSizes";
import DropDownContentSelector from "./AlertsDropDownContentSelector";
import NewTabWithIcon from "../ReusableComponents/HeadlessUiTabWithIcon/NewTabWithIcon";
import { useDashboardAlertActions } from "./useDashboardAlertActions";
import { type ApiCompanySummary } from "../../models/api/admin/company";
import { type ApiDepartmentSummary } from "../../models/api/admin/department";

export type AlertsSectionContentType = {
    tab: DashboardCategoriesType;
    title: DashboardContentSections;
    icon: JSX.Element;
    content: JSX.Element;
    count?: number;
};

export type DashboardAlertSectionProps = {
    alertsData?: DashboardAlertsData;
    isLoadingAlerts: boolean;
    selectedTab: DashboardCategoriesType;
    setSelectedTab: React.Dispatch<
        React.SetStateAction<DashboardCategoriesType>
    >;
    selectedCompany: ApiCompanySummary | null;
    selectedDepartment: ApiDepartmentSummary | null;
};

const DashboardAlertsSection: React.FC<DashboardAlertSectionProps> = ({
    alertsData,
    isLoadingAlerts,
    selectedTab,
    setSelectedTab,
    selectedCompany,
    selectedDepartment,
}) => {
    const isBiggerDisplay = useIsExtraLargeScreen();
    const {
        driverAlertAction,
        professionalDriverAlertAction,
        driverDeclarationAlertAction,
        licenceCheckAlertAction,
        greyFleetAlertAction,
    } = useDashboardAlertActions(
        selectedCompany?.companyId ?? null,
        selectedDepartment?.departmentId ?? null
    );

    const alertsSectionContent: AlertsSectionContentType[] = [
        {
            tab: DashboardCategories.Drivers,
            title: "All Drivers",
            icon: <IdentificationCard size={24} weight="duotone" />,
            content: (
                <DashboardAlertsTabContents
                    alertsData={alertsData?.driverAlerts}
                    isLoadingAlerts={isLoadingAlerts}
                    action={driverAlertAction}
                />
            ),
        },
        {
            tab: DashboardCategories.ProfessionalDrivers,
            title: "Professional Drivers",
            icon: <Truck size={24} weight="duotone" />,
            content: (
                <DashboardAlertsTabContents
                    alertsData={alertsData?.professionalDriverAlerts}
                    isLoadingAlerts={isLoadingAlerts}
                    action={professionalDriverAlertAction}
                />
            ),
        },
        {
            tab: DashboardCategories.Dvla,
            title: "DVLA Checks",
            icon: <CheckCircle size={24} weight="duotone" />,
            content: (
                <DashboardAlertsTabContents
                    alertsData={alertsData?.licenceCheckAlerts}
                    isLoadingAlerts={isLoadingAlerts}
                    action={licenceCheckAlertAction}
                />
            ),
        },
        {
            tab: DashboardCategories.GreyFleet,
            title: "Grey Fleet",
            icon: <Car size={24} weight="duotone" />,
            content: (
                <DashboardAlertsTabContents<GreyFleetAlertTitles>
                    alertsData={alertsData?.greyFleetAlerts}
                    isLoadingAlerts={isLoadingAlerts}
                    action={greyFleetAlertAction}
                />
            ),
        },
        {
            tab: DashboardCategories.DriverDeclarations,
            title: "Driver Declarations",
            icon: <Scroll size={24} weight="duotone" />,
            content: (
                <DashboardAlertsTabContents
                    alertsData={alertsData?.driverDeclarationAlerts}
                    isLoadingAlerts={isLoadingAlerts}
                    action={driverDeclarationAlertAction}
                />
            ),
        },
    ];

    return (
        <section className="mt-4 flex-1 2xl:border-r" about="alerts">
            {!isBiggerDisplay && (
                <DropDownContentSelector
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    isLoading={isLoadingAlerts}
                    alertsData={alertsData}
                    actions={{
                        driverAlertAction,
                        professionalDriverAlertAction,
                        driverDeclarationAlertAction,
                        licenceCheckAlertAction,
                        greyFleetAlertAction,
                    }}
                />
            )}

            {isBiggerDisplay && (
                <Tab.Group>
                    <Tab.List
                        className={clsx(
                            "mb-4 flex min-w-max flex-row divide-x-2 divide-S2D-neutral-10 rounded-sm border-2 border-S2D-neutral-10"
                        )}
                    >
                        {alertsSectionContent.map((content) => (
                            <div
                                key={content.title}
                                className="w-1/4 flex-shrink flex-grow"
                                onClick={() => setSelectedTab(content.tab)}
                            >
                                <NewTabWithIcon
                                    icon={content.icon}
                                    title={content.title}
                                    count={content.count}
                                />
                            </div>
                        ))}
                    </Tab.List>
                    <Tab.Panels>
                        {alertsSectionContent.map((content) => (
                            <Tab.Panel key={`${content.title}-content`}>
                                {content.content}
                            </Tab.Panel>
                        ))}
                    </Tab.Panels>
                </Tab.Group>
            )}
        </section>
    );
};

export default DashboardAlertsSection;
